import { AxiosHeaders, type AxiosInstance, type AxiosRequestConfig } from 'axios'

export type EtagResult<T> = {
  data: T
  etag: string | null
  notModified: boolean
}

const inFlightRequests = new WeakMap<AxiosInstance, Map<string, Promise<EtagResult<unknown>>>>()

function normalizeEtag(value: unknown): string | null {
  if (typeof value !== 'string') return null

  const etag = value.trim()
  return etag || null
}

function requestKey(
  url: string,
  config: AxiosRequestConfig,
  etag: string | null | undefined,
) {
  const params = config.params instanceof URLSearchParams
    ? config.params.toString()
    : JSON.stringify(config.params ?? {})
  const headers = AxiosHeaders.from(config.headers as AxiosHeaders | undefined).toJSON()

  return JSON.stringify({ url, params, headers, etag: etag ?? null })
}

/**
 * Performs an explicit conditional GET. The current data is returned for a
 * 304 response so callers never have to replace hydrated state unnecessarily.
 */
export async function getWithEtag<T>(
  client: AxiosInstance,
  url: string,
  config: AxiosRequestConfig,
  etag: string | null | undefined,
  currentData: T,
): Promise<EtagResult<T>> {
  const requests = inFlightRequests.get(client) ?? new Map<string, Promise<EtagResult<unknown>>>()
  const key = requestKey(url, config, etag)
  const existing = requests.get(key)

  if (existing) {
    const result = await existing as EtagResult<T>

    return result.notModified
      ? { ...result, data: currentData }
      : result
  }

  inFlightRequests.set(client, requests)

  const request = performRequest(client, url, config, etag, currentData)
  requests.set(key, request)

  try {
    return await request
  } finally {
    requests.delete(key)
  }
}

async function performRequest<T>(
  client: AxiosInstance,
  url: string,
  config: AxiosRequestConfig,
  etag: string | null | undefined,
  currentData: T,
): Promise<EtagResult<T>> {
  const headers = AxiosHeaders.from(config.headers as AxiosHeaders | undefined)

  if (etag) {
    headers.set('If-None-Match', etag)
  }

  const response = await client.get<T>(url, {
    ...config,
    headers,
    ...(typeof window !== 'undefined'
      ? {
          adapter: 'fetch',
          fetchOptions: {
            // The SSG validator and Pinia cache are authoritative. Bypass the
            // browser cache so conditional requests expose the API's 304 and
            // resources without an ETag still receive a current 200 response.
            cache: 'no-store',
          },
        }
      : {}),
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  })
  const responseEtag = normalizeEtag(response.headers.etag) ?? etag ?? null

  if (response.status === 304) {
    return {
      data: currentData,
      etag: responseEtag,
      notModified: true,
    }
  }

  return {
    data: response.data,
    etag: normalizeEtag(response.headers.etag),
    notModified: false,
  }
}
